

import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE_URL, API_DOMAIN } from "../utils/config";
import { useToast } from "../context/ToastContext";
import { 
  FiPlus, FiEdit, FiTrash2, FiAward, 
  FiBriefcase, FiX,
  FiFileText, FiUpload, FiInfo
} from "react-icons/fi";
import "./EmployeeDashboard.css";

const CandidateEducation = () => {
  const { showToast } = useToast();

  const [educationList, setEducationList] = useState([]);
  const [isEduModalOpen, setIsEduModalOpen] = useState(false);
  const [eduFormData, setEduFormData] = useState({
    qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: ""
  });
  const [editingEduId, setEditingEduId] = useState(null);

  const [experienceList, setExperienceList] = useState([]);
  const [isExpModalOpen, setIsExpModalOpen] = useState(false);
  const [expFormData, setExpFormData] = useState({
    companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', 
    offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null
  });
  const [editingExpId, setEditingExpId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("candidateToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getCandidateId = () => localStorage.getItem("candidateId") || null;

  // Validation helper functions
  const validateAlphabetsOnly = (value) => {
    return /^[A-Za-z\s]*$/.test(value);
  };

  const validateNumbersOnly = (value) => {
    return /^\d*$/.test(value);
  };

  const validatePercentage = (value) => {
    return /^\d{0,3}(\.\d{0,2})?$/.test(value);
  };

  // Input handlers with validation and length limits
  const handleEduInputChange = (field, value) => {
    // For Institution - only alphabets and spaces, max 100 characters
    if (field === 'institution') {
      if (value === '' || validateAlphabetsOnly(value)) {
        if (value.length <= 100) {
          setEduFormData({...eduFormData, [field]: value});
        }
      }
      return;
    }

    // For Qualification - alphabets, numbers, and common symbols, max 100 characters
    if (field === 'qualification') {
      if (value === '' || /^[A-Za-z0-9\s.,()/-]*$/.test(value)) {
        if (value.length <= 100) {
          setEduFormData({...eduFormData, [field]: value});
        }
      }
      return;
    }

    // For Location - alphabets, numbers, spaces, and common punctuation, max 100 characters
    if (field === 'location') {
      if (value === '' || /^[A-Za-z0-9\s,.-]*$/.test(value)) {
        if (value.length <= 100) {
          setEduFormData({...eduFormData, [field]: value});
        }
      }
      return;
    }

    // For Passing Year - only numbers, exactly 4 digits
    if (field === 'passingYear') {
      if (value === '' || validateNumbersOnly(value)) {
        if (value.length <= 4) {
          setEduFormData({...eduFormData, [field]: value});
        }
      }
      return;
    }

    // For Percentage - numbers and decimal point, max 10 characters
    if (field === 'percentage') {
      if (value === '' || validatePercentage(value)) {
        if (value.length <= 10) {
          setEduFormData({...eduFormData, [field]: value});
        }
      }
      return;
    }

    // For other fields (dates)
    setEduFormData({...eduFormData, [field]: value});
  };

  // Experience form input handlers with length limits
  const handleExpInputChange = (field, value) => {
    // For Company Name - alphabets, numbers, spaces, and common symbols, max 100 characters
    if (field === 'companyName') {
      if (value === '' || /^[A-Za-z0-9\s&.-]*$/.test(value)) {
        if (value.length <= 100) {
          setExpFormData({...expFormData, [field]: value});
        }
      }
      return;
    }

    // For Role - alphabets, numbers, spaces, and common symbols, max 100 characters
    if (field === 'role') {
      if (value === '' || /^[A-Za-z0-9\s.,/-]*$/.test(value)) {
        if (value.length <= 100) {
          setExpFormData({...expFormData, [field]: value});
        }
      }
      return;
    }

    // For Location - alphabets, numbers, spaces, and common punctuation, max 100 characters
    if (field === 'location') {
      if (value === '' || /^[A-Za-z0-9\s,.-]*$/.test(value)) {
        if (value.length <= 100) {
          setExpFormData({...expFormData, [field]: value});
        }
      }
      return;
    }

    // For Salary - numbers, commas, and decimal point, max 20 characters
    if (field === 'salary') {
      if (value === '' || /^[\d,.]*$/.test(value)) {
        if (value.length <= 20) {
          setExpFormData({...expFormData, [field]: value});
        }
      }
      return;
    }

    // For other fields (dates)
    setExpFormData({...expFormData, [field]: value});
  };

  // Helper function to format date using local time
  const formatDateLocal = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "";
    }
  };

  const renderDocumentUrl = (path) => {
    if (!path) return "#";
    if (path.startsWith("http")) return path;
    const normalizedPath = path.replace(/\\/g, '/');
    if (normalizedPath.includes('/uploads/')) {
      const parts = normalizedPath.split('/uploads/');
      return `${API_DOMAIN}/uploads/${parts[1]}`;
    }
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
    return `${API_DOMAIN}/${cleanPath}`;
  };

  const fetchData = async () => {
    setLoading(true);
    const candidateId = getCandidateId();
    const headers = getAuthHeaders();

    // Education - alag try catch
    try {
      const eduRes = await axios.get(`${API_BASE_URL}/candidate/education`, { headers, params: { candidateId } });
      setEducationList(eduRes.data?.data || eduRes.data || []);
    } catch (err) {
      console.error("Education fetch failed:", err.message);
      setEducationList([]);
    }

    // Experience - alag try catch, education fail hone se affect nahi hoga
    try {
      const expRes = await axios.get(`${API_BASE_URL}/candidate/experience`, { headers, params: { candidateId } });
      setExperienceList(expRes.data?.data || expRes.data || []);
    } catch (err) {
      console.error("Experience fetch failed:", err.message);
      setExperienceList([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEduSubmit = async (e) => {
    e.preventDefault();
    
    // Validate Institution - only alphabets and spaces, min 2 max 100 characters
    if (!eduFormData.institution || eduFormData.institution.trim().length < 2) {
      showToast("Institution name must be at least 2 characters", "error");
      return;
    }
    if (eduFormData.institution.trim().length > 100) {
      showToast("Institution name cannot exceed 100 characters", "error");
      return;
    }
    if (!validateAlphabetsOnly(eduFormData.institution)) {
      showToast("Institution name should contain only alphabets and spaces", "error");
      return;
    }

    // Validate Qualification - min 2 max 100 characters
    if (!eduFormData.qualification || eduFormData.qualification.trim().length < 2) {
      showToast("Qualification must be at least 2 characters", "error");
      return;
    }
    if (eduFormData.qualification.trim().length > 100) {
      showToast("Qualification cannot exceed 100 characters", "error");
      return;
    }

    // Validate Passing Year - exactly 4 digits
    if (!eduFormData.passingYear || eduFormData.passingYear.length !== 4) {
      showToast("Passing year must be exactly 4 digits", "error");
      return;
    }
    if (!validateNumbersOnly(eduFormData.passingYear)) {
      showToast("Passing year should contain only numbers", "error");
      return;
    }

    // Validate Percentage - only numbers with optional decimal
    if (!eduFormData.percentage || eduFormData.percentage.trim() === "") {
      showToast("Percentage is required", "error");
      return;
    }
    if (!validatePercentage(eduFormData.percentage)) {
      showToast("Percentage should contain only numbers and optional decimal point", "error");
      return;
    }
    const percentageNum = parseFloat(eduFormData.percentage);
    if (isNaN(percentageNum) || percentageNum < 0 || percentageNum > 100) {
      showToast("Percentage must be between 0 and 100", "error");
      return;
    }

    // Validate Location - min 2 max 100 characters
    if (!eduFormData.location || eduFormData.location.trim().length < 2) {
      showToast("Location must be at least 2 characters", "error");
      return;
    }
    if (eduFormData.location.trim().length > 100) {
      showToast("Location cannot exceed 100 characters", "error");
      return;
    }

    // Validate dates: Start Date must be earlier than End Date
    if (eduFormData.startDate && eduFormData.endDate) {
      const startDate = new Date(eduFormData.startDate);
      const endDate = new Date(eduFormData.endDate);
      if (startDate >= endDate) {
        showToast("Start Date must be earlier than End Date", "error");
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      const payload = { ...eduFormData, candidateId: getCandidateId() };
      let res;
      if (editingEduId) {
        res = await axios.put(`${API_BASE_URL}/candidate/education/${editingEduId}`, payload, { headers: getAuthHeaders() });
      } else {
        res = await axios.post(`${API_BASE_URL}/candidate/education`, payload, { headers: getAuthHeaders() });
      }
      if (res.data.success) {
        showToast("Academic record synchronized", "success");
        fetchData();
        setIsEduModalOpen(false);
        setEditingEduId(null);
        setEduFormData({ qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: "" });
      }
    } catch (err) {
      showToast("Transmission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEduEdit = (edu) => {
    setEditingEduId(edu._id);
    setEduFormData({
      qualification: edu.qualification || "",
      institution: edu.institution || "",
      startDate: edu.startDate ? formatDateLocal(edu.startDate) : "",
      endDate: edu.endDate ? formatDateLocal(edu.endDate) : "",
      passingYear: edu.passingYear || "",
      location: edu.location || "",
      percentage: edu.percentage || ""
    });
    setIsEduModalOpen(true);
  };

  const handleEduDelete = async (id) => {
    if (!window.confirm("Purge this academic record?")) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/candidate/education/${id}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        showToast("Record purged", "success");
        fetchData();
      }
    } catch (err) {
      showToast("Action failed", "error");
    }
  };

  const handleExpSubmit = async (e) => {
    e.preventDefault();
    
    // Validate Company Name - min 2 max 100 characters
    if (!expFormData.companyName || expFormData.companyName.trim().length < 2) {
      showToast("Company name must be at least 2 characters", "error");
      return;
    }
    if (expFormData.companyName.trim().length > 100) {
      showToast("Company name cannot exceed 100 characters", "error");
      return;
    }

    // Validate Role - min 2 max 100 characters
    if (!expFormData.role || expFormData.role.trim().length < 2) {
      showToast("Role must be at least 2 characters", "error");
      return;
    }
    if (expFormData.role.trim().length > 100) {
      showToast("Role cannot exceed 100 characters", "error");
      return;
    }

    // Validate Location - min 2 max 100 characters
    if (!expFormData.location || expFormData.location.trim().length < 2) {
      showToast("Location must be at least 2 characters", "error");
      return;
    }
    if (expFormData.location.trim().length > 100) {
      showToast("Location cannot exceed 100 characters", "error");
      return;
    }

    // Validate Salary - only numbers
    if (!expFormData.salary || expFormData.salary.trim() === "") {
      showToast("Salary is required", "error");
      return;
    }
    const salaryClean = expFormData.salary.replace(/,/g, '');
    if (!validateNumbersOnly(salaryClean)) {
      showToast("Salary should contain only numbers", "error");
      return;
    }
    if (salaryClean.length > 20) {
      showToast("Salary cannot exceed 20 digits", "error");
      return;
    }

    // Validate Start Date
    if (!expFormData.startDate) {
      showToast("Start date is required", "error");
      return;
    }

    // Validate dates: Start Date must be earlier than End Date (if end date exists)
    if (expFormData.startDate && expFormData.endDate) {
      const startDate = new Date(expFormData.startDate);
      const endDate = new Date(expFormData.endDate);
      if (startDate >= endDate) {
        showToast("Start Date must be earlier than End Date", "error");
        return;
      }
    }

    setIsSubmitting(true);
    const data = new FormData();
    Object.keys(expFormData).forEach(key => {
      if (expFormData[key]) data.append(key, expFormData[key]);
    });
    data.append("candidateId", getCandidateId());

    try {
      let res;
      const headers = { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() };
      if (editingExpId) {
        res = await axios.put(`${API_BASE_URL}/candidate/experience/${editingExpId}`, data, { headers });
      } else {
        res = await axios.post(`${API_BASE_URL}/candidate/experience`, data, { headers });
      }
      if (res.data?.success || res.status === 200 || res.status === 201) {
        showToast("Professional record synchronized", "success");
        fetchData();
        setIsExpModalOpen(false);
        setEditingExpId(null);
        setExpFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null });
      }
    } catch (err) {
      showToast("Transmission failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExpEdit = (exp) => {
    setEditingExpId(exp._id);
    setExpFormData({
      companyName: exp.companyName || '',
      role: exp.role || '',
      location: exp.location || '',
      salary: exp.salary || '',
      startDate: exp.startDate ? formatDateLocal(exp.startDate) : '',
      endDate: exp.endDate ? formatDateLocal(exp.endDate) : '',
      offerLetter: exp.offerLetter || null,
      payslip: exp.payslip || null,
      relievingLetter: exp.relievingLetter || null,
      otherDocument: exp.otherDocument || null
    });
    setIsExpModalOpen(true);
  };

  const handleExpDelete = async (id) => {
    if (!window.confirm("Purge this professional record?")) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/candidate/experience/${id}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        showToast("Record purged", "success");
        fetchData();
      }
    } catch (err) {
      showToast("Action failed", "error");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'PRESENT';

  // Character count helper
  const getCharCount = (value) => {
    return value ? value.length : 0;
  };

  return (
    <>
      {/* Dashboard Header */}
      <div className="emp-dash__header">
        <div>
          <h1 className="emp-dash__greeting">
            Academic & <span>Professional</span>
          </h1>
          <p className="emp-dash__subtitle">
            Education and career history records
          </p>
        </div>
        <div className="emp-dash__date-pill">
          <FiAward />
          <span>
            {new Date().toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      </div>

      {/* Educational Qualifications */}
      <div className="emp-dash__card mb-6">
        <div className="emp-dash__card-header">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner border border-emerald-200/50">
              <FiAward size={24} />
            </div>
            <div>
              <h3 className="emp-dash__card-title">Educational Qualifications</h3>
              <p className="emp-dash__card-desc">Academic history & credentials</p>
            </div>
          </div>
          <button onClick={() => setIsEduModalOpen(true)} className="h-11 px-6 bg-indigo-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2">
            <FiPlus size={12} /> Add Qualification
          </button>
        </div>
        <div className="emp-dash__card-body bg-gray-50/50">
          <div className="overflow-x-auto">
            <table className="min-w-[1000px]">
              <thead className="text-[11px] text-gray-700 uppercase tracking-widest font-bold border-b border-gray-200">
                <tr>
                  <th className="py-4 px-6 text-left font-bold">Qualification</th>
                  <th className="py-4 px-6 text-left font-bold">Institution</th>
                  <th className="py-4 px-6 text-center font-bold">Period / Year</th>
                  <th className="py-4 px-6 text-center font-bold">Location</th>
                  <th className="py-4 px-6 text-center font-bold">Score (%)</th>
                  <th className="py-4 px-6 text-center font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Records...</p>
                    </td>
                  </tr>
                ) : educationList.length > 0 ? (
                  educationList.map((edu) => (
                    <tr key={edu._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{edu.qualification}</span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Degree</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{edu.institution}</span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Institution</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="text-xs font-bold text-gray-800">{edu.passingYear}</div>
                        {edu.startDate && <div className="text-[8px] font-bold text-gray-400 mt-0.5">{new Date(edu.startDate).getFullYear()} - {new Date(edu.endDate).getFullYear()}</div>}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="text-[10px] font-bold text-gray-600 uppercase">{edu.location}</span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-widest border border-emerald-100 shadow-sm">
                          {edu.percentage}%
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex justify-center gap-3">
                          <button onClick={() => handleEduEdit(edu)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm">
                            <FiEdit size={16} />
                          </button>
                          <button onClick={() => handleEduDelete(edu._id)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-100 transition-all transform active:scale-90 shadow-sm">
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="p-16 text-center">
                        <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-emerald-200 border border-emerald-100/50 shadow-sm">
                          <FiAward size={30} />
                        </div>
                        <h3 className="text-base font-bold text-gray-700 mb-1">No Educational Records</h3>
                        <p className="text-[11px] text-gray-400 font-bold tracking-widest uppercase max-w-xs mx-auto leading-relaxed">Use the button above to add your first educational qualification.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Professional Experience */}
      <div className="emp-dash__card mb-6">
        <div className="emp-dash__card-header">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 flex items-center justify-center shadow-inner border border-blue-200/50">
              <FiBriefcase size={24} />
            </div>
            <div>
              <h3 className="emp-dash__card-title">Professional Experience</h3>
              <p className="emp-dash__card-desc">Career history & documents</p>
            </div>
          </div>
          <button onClick={() => setIsExpModalOpen(true)} className="h-11 px-6 bg-indigo-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2">
            <FiPlus size={12} /> Add Experience
          </button>
        </div>
        <div className="emp-dash__card-body bg-gray-50/50">
          <div className="overflow-x-auto">
            <table className="min-w-[1000px]">
              <thead className="text-[11px] text-gray-700 uppercase tracking-widest font-bold border-b border-gray-200">
                <tr>
                  <th className="py-4 px-6 text-left font-bold">Company & Role</th>
                  <th className="py-4 px-6 text-left font-bold">Location</th>
                  <th className="py-4 px-6 text-center font-bold">Tenure Period</th>
                  <th className="py-4 px-6 text-center font-bold">Salary</th>
                  <th className="py-4 px-6 text-center font-bold">Documents</th>
                  <th className="py-4 px-6 text-center font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest animate-pulse">Loading Records...</p>
                    </td>
                  </tr>
                ) : experienceList.length > 0 ? (
                  experienceList.map((exp) => (
                    <tr key={exp._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{exp.role}</span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{exp.companyName}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-tight">{exp.location}</span>
                          <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Location</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="text-xs font-bold text-gray-800">{formatDate(exp.startDate)} — {formatDate(exp.endDate)}</div>
                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 block">Tenure</span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-widest border border-blue-100 shadow-sm">
                          ₹{exp.salary}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex justify-center gap-3">
                          {exp.offerLetter && <a href={renderDocumentUrl(exp.offerLetter)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm" title="Offer Letter"><FiFileText size={16} /></a>}
                          {exp.payslip && <a href={renderDocumentUrl(exp.payslip)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white border border-emerald-100 transition-all transform active:scale-90 shadow-sm" title="Payslip"><FiFileText size={16} /></a>}
                          {exp.relievingLetter && <a href={renderDocumentUrl(exp.relievingLetter)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-orange-50 text-orange-700 hover:bg-orange-600 hover:text-white border border-orange-100 transition-all transform active:scale-90 shadow-sm" title="Relieving Letter"><FiFileText size={16} /></a>}
                          {exp.otherDocument && <a href={renderDocumentUrl(exp.otherDocument)} target="_blank" rel="noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-purple-50 text-purple-700 hover:bg-purple-600 hover:text-white border border-purple-100 transition-all transform active:scale-90 shadow-sm" title="Other Document"><FiFileText size={16} /></a>}
                          {!exp.offerLetter && !exp.payslip && !exp.relievingLetter && !exp.otherDocument && <span className="text-[9px] font-bold text-gray-300 uppercase">No Docs</span>}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex justify-center gap-3">
                          <button onClick={() => handleExpEdit(exp)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm">
                            <FiEdit size={16} />
                          </button>
                          <button onClick={() => handleExpDelete(exp._id)} className="w-10 h-10 inline-flex items-center justify-center rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-100 transition-all transform active:scale-90 shadow-sm">
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="p-16 text-center">
                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-200 border border-blue-100/50 shadow-sm">
                          <FiBriefcase size={30} />
                        </div>
                        <h3 className="text-base font-bold text-gray-700 mb-1">No Professional Records</h3>
                        <p className="text-[11px] text-gray-400 font-bold tracking-widest uppercase max-w-xs mx-auto leading-relaxed">Use the button above to add your first professional experience.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
        <FiInfo className="text-blue-400" />
        Timely Health Academic & Professional Records • {new Date().getFullYear()}
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {/* Education Modal */}
        {isEduModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-xl rounded-3xl md:rounded-[3rem] shadow-2xl p-6 md:p-12 border border-gray-100 relative max-h-[95vh] overflow-y-auto">
              <button className="absolute top-4 md:top-8 right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100"
                onClick={() => { setIsEduModalOpen(false); setEditingEduId(null); setEduFormData({ qualification: "", institution: "", startDate: "", endDate: "", passingYear: "", location: "", percentage: "" }); }}>
                <FiX size={16} />
              </button>
              <div className="mb-8 md:mb-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-emerald-50 text-emerald-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-5 shadow-inner border border-emerald-100"><FiAward size={30} /></div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-[0.1em]">{editingEduId ? "Update Academy" : "Log Academy"}</h3>
              </div>
              <form onSubmit={handleEduSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Qualification *</label>
                  <input 
                    type="text" 
                    required 
                    value={eduFormData.qualification} 
                    onChange={e => handleEduInputChange('qualification', e.target.value)} 
                    maxLength={25} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" 
                    placeholder="e.g. Bachelor of Engineering" 
                    title="Alphabets, numbers, spaces, . , ( ) / - allowed (Max 25 chars)"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] text-gray-400">Alphabets, numbers, spaces, . , ( ) / - allowed</p>
                    <span className={`text-[8px] font-bold ${getCharCount(eduFormData.qualification) > 20 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {getCharCount(eduFormData.qualification)}/25
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Institution *</label>
                  <input 
                    type="text" 
                    required 
                    value={eduFormData.institution} 
                    onChange={e => handleEduInputChange('institution', e.target.value)} 
                    maxLength={20} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" 
                    placeholder="University Name" 
                    title="Only alphabets and spaces allowed (Max 20 chars)"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] text-gray-400">Only alphabets and spaces allowed</p>
                    <span className={`text-[8px] font-bold ${getCharCount(eduFormData.institution) > 15 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {getCharCount(eduFormData.institution)}/20
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input type="date" value={eduFormData.startDate} onChange={e => setEduFormData({...eduFormData, startDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                  <input type="date" value={eduFormData.endDate} onChange={e => setEduFormData({...eduFormData, endDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Passing Year *</label>
                  <input 
                    type="text" 
                    required 
                    value={eduFormData.passingYear} 
                    onChange={e => handleEduInputChange('passingYear', e.target.value)} 
                    maxLength={4} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" 
                    placeholder="2024" 
                    title="Exactly 4 digits"
                  />
                  <p className="text-[8px] text-gray-400">Exactly 4 digits</p>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Location *</label>
                  <input 
                    type="text" 
                    required 
                    value={eduFormData.location} 
                    onChange={e => handleEduInputChange('location', e.target.value)} 
                    maxLength={20} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" 
                    placeholder="City, State" 
                    title="Alphabets, numbers, spaces, . , - allowed (Max 20 chars)"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] text-gray-400">Alphabets, numbers, spaces, . , - allowed</p>
                    <span className={`text-[8px] font-bold ${getCharCount(eduFormData.location) > 19 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {getCharCount(eduFormData.location)}/20
                    </span>
                  </div>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Score (%) *</label>
                  <input 
                    type="text" 
                    required 
                    value={eduFormData.percentage} 
                    onChange={e => handleEduInputChange('percentage', e.target.value)} 
                    maxLength={10} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none" 
                    placeholder="85.5" 
                    title="Numbers and decimal point only (0-100)"
                  />
                  <p className="text-[8px] text-gray-400">Numbers only, between 0-100</p>
                </div>
                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-2xl text-[11px] font-bold uppercase tracking-[0.3em] shadow-xl shadow-emerald-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                    {isSubmitting ? "Transmitting..." : editingEduId ? "Update Records" : "Save Records"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Experience Modal */}
        {isExpModalOpen && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-2xl rounded-3xl md:rounded-[3rem] shadow-2xl p-6 md:p-12 border border-gray-100 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
              <button className="absolute top-4 md:top-8 right-4 md:right-8 w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100"
                onClick={() => { setIsExpModalOpen(false); setEditingExpId(null); setExpFormData({ companyName: '', role: '', location: '', salary: '', startDate: '', endDate: '', offerLetter: null, payslip: null, relievingLetter: null, otherDocument: null }); }}>
                <FiX size={16} />
              </button>
              <div className="mb-8 md:mb-10 flex flex-col items-center text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 text-blue-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center mb-4 md:mb-5 shadow-inner border border-blue-100"><FiBriefcase size={30} /></div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-[0.1em]">{editingExpId ? "Update Career" : "Log Career"}</h3>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-2">Professional Sync Protocol</p>
              </div>
              <form onSubmit={handleExpSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Company Name *</label>
                  <input 
                    type="text" 
                    required 
                    value={expFormData.companyName} 
                    onChange={e => handleExpInputChange('companyName', e.target.value)} 
                    maxLength={25} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" 
                    placeholder="Acme Corp" 
                    title="Alphabets, numbers, spaces, & . - allowed (Max 25 chars)"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] text-gray-400">Alphabets, numbers, spaces, & . - allowed</p>
                    <span className={`text-[8px] font-bold ${getCharCount(expFormData.companyName) > 25 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {getCharCount(expFormData.companyName)}/25
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Role *</label>
                  <input 
                    type="text" 
                    required 
                    value={expFormData.role} 
                    onChange={e => handleExpInputChange('role', e.target.value)} 
                    maxLength={25} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" 
                    placeholder="Sr. Engineer" 
                    title="Alphabets, numbers, spaces, . , / - allowed (Max 25 chars)"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] text-gray-400">Alphabets, numbers, spaces, . , / - allowed</p>
                    <span className={`text-[8px] font-bold ${getCharCount(expFormData.role) > 25 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {getCharCount(expFormData.role)}/25
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Location *</label>
                  <input 
                    type="text" 
                    required 
                    value={expFormData.location} 
                    onChange={e => handleExpInputChange('location', e.target.value)} 
                    maxLength={20} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" 
                    placeholder="City, Country" 
                    title="Alphabets, numbers, spaces, . , - allowed (Max 100 chars)"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[8px] text-gray-400">Alphabets, numbers, spaces, . , - allowed</p>
                    <span className={`text-[8px] font-bold ${getCharCount(expFormData.location) > 19 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {getCharCount(expFormData.location)}/20
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Salary *</label>
                  <input 
                    type="text" 
                    required 
                    value={expFormData.salary} 
                    onChange={e => handleExpInputChange('salary', e.target.value)} 
                    maxLength={20} 
                    className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none" 
                    placeholder="e.g. 50,000" 
                    title="Numbers, commas, and decimal point only (Max 20 chars)"
                  />
                  <p className="text-[8px] text-gray-400">Numbers, commas, and decimal point only</p>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date *</label>
                  <input type="date" required value={expFormData.startDate} onChange={e => setExpFormData({...expFormData, startDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-[10px] font-bold outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">End Date (Current)</label>
                  <input type="date" value={expFormData.endDate} onChange={e => setExpFormData({...expFormData, endDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-[10px] font-bold outline-none" />
                </div>

                <div className="md:col-span-2 grid grid-cols-2 gap-4 mt-4">
                  <div className="p-5 bg-indigo-50/50 border-2 border-dashed border-indigo-200 rounded-3xl flex flex-col items-center gap-2 hover:bg-indigo-100 transition relative">
                    {typeof expFormData.offerLetter === 'string' && expFormData.offerLetter ? (
                      <div className="flex flex-col items-center gap-2 z-10">
                        <a href={renderDocumentUrl(expFormData.offerLetter)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 text-[8px] font-bold uppercase tracking-widest underline" onClick={(e) => e.stopPropagation()}>View Existing</a>
                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Or replace:</span>
                      </div>
                    ) : (
                      <FiUpload className="text-indigo-500 z-10" />
                    )}
                    <span className="text-[9px] font-bold text-indigo-700 uppercase tracking-widest text-center z-10">{typeof expFormData.offerLetter === 'object' && expFormData.offerLetter ? expFormData.offerLetter.name : (typeof expFormData.offerLetter === 'string' && expFormData.offerLetter ? "REPLACE OFFER LETTER" : "OFFER LETTER")}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, offerLetter: e.target.files[0]})} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="absolute inset-0 opacity-0 cursor-pointer z-0" />
                  </div>
                  <div className="p-5 bg-emerald-50/50 border-2 border-dashed border-emerald-200 rounded-3xl flex flex-col items-center gap-2 hover:bg-emerald-100 transition relative">
                    {typeof expFormData.payslip === 'string' && expFormData.payslip ? (
                      <div className="flex flex-col items-center gap-2 z-10">
                        <a href={renderDocumentUrl(expFormData.payslip)} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-800 text-[8px] font-bold uppercase tracking-widest underline" onClick={(e) => e.stopPropagation()}>View Existing</a>
                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Or replace:</span>
                      </div>
                    ) : (
                      <FiUpload className="text-emerald-500 z-10" />
                    )}
                    <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-widest text-center z-10">{typeof expFormData.payslip === 'object' && expFormData.payslip ? expFormData.payslip.name : (typeof expFormData.payslip === 'string' && expFormData.payslip ? "REPLACE PAYSLIP" : "PAYSLIP")}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, payslip: e.target.files[0]})} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="absolute inset-0 opacity-0 cursor-pointer z-0" />
                  </div>
                  <div className="p-5 bg-orange-50/50 border-2 border-dashed border-orange-200 rounded-3xl flex flex-col items-center gap-2 hover:bg-orange-100 transition relative">
                    {typeof expFormData.relievingLetter === 'string' && expFormData.relievingLetter ? (
                      <div className="flex flex-col items-center gap-2 z-10">
                        <a href={renderDocumentUrl(expFormData.relievingLetter)} target="_blank" rel="noreferrer" className="text-orange-600 hover:text-orange-800 text-[8px] font-bold uppercase tracking-widest underline" onClick={(e) => e.stopPropagation()}>View Existing</a>
                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Or replace:</span>
                      </div>
                    ) : (
                      <FiUpload className="text-orange-500 z-10" />
                    )}
                    <span className="text-[9px] font-bold text-orange-700 uppercase tracking-widest text-center z-10">{typeof expFormData.relievingLetter === 'object' && expFormData.relievingLetter ? expFormData.relievingLetter.name : (typeof expFormData.relievingLetter === 'string' && expFormData.relievingLetter ? "REPLACE RELIEVING LETTER" : "RELIEVING LETTER")}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, relievingLetter: e.target.files[0]})} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="absolute inset-0 opacity-0 cursor-pointer z-0" />
                  </div>
                  <div className="p-5 bg-purple-50/50 border-2 border-dashed border-purple-200 rounded-3xl flex flex-col items-center gap-2 hover:bg-purple-100 transition relative">
                    {typeof expFormData.otherDocument === 'string' && expFormData.otherDocument ? (
                      <div className="flex flex-col items-center gap-2 z-10">
                        <a href={renderDocumentUrl(expFormData.otherDocument)} target="_blank" rel="noreferrer" className="text-purple-600 hover:text-purple-800 text-[8px] font-bold uppercase tracking-widest underline" onClick={(e) => e.stopPropagation()}>View Existing</a>
                        <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Or replace:</span>
                      </div>
                    ) : (
                      <FiUpload className="text-purple-500 z-10" />
                    )}
                    <span className="text-[9px] font-bold text-purple-700 uppercase tracking-widest text-center z-10">{typeof expFormData.otherDocument === 'object' && expFormData.otherDocument ? expFormData.otherDocument.name : (typeof expFormData.otherDocument === 'string' && expFormData.otherDocument ? "REPLACE OTHER DOCUMENT" : "OTHER DOCUMENT")}</span>
                    <input type="file" onChange={e => setExpFormData({...expFormData, otherDocument: e.target.files[0]})} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="absolute inset-0 opacity-0 cursor-pointer z-0" />
                  </div>
                </div>

                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[11px] font-bold uppercase tracking-[0.3em] shadow-xl shadow-blue-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                    {isSubmitting ? "Transmitting..." : editingExpId ? "Update Career" : "Authorize Log"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-center gap-3 text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mt-10">
        <FiInfo className="text-indigo-400" />
        Synchronized Professional & Academic Records Protocol • {new Date().getFullYear()}
      </div>
    </>
  );
};

export default CandidateEducation;